import type { CSSProperties } from 'react'

interface SkeletonProps {
  className?: string
  style?: CSSProperties
}

export function Skeleton({ className = '', style }: SkeletonProps) {
  return (
    <div
      className={`animate-pulse ${className}`}
      style={{
        backgroundColor: '#E9E9EA',
        borderRadius: '6px',
        ...style,
      }}
    />
  )
}

export function MetricCardSkeleton() {
  return (
    <div
      className="bg-white rounded-lg"
      style={{
        width: '100%',
        minHeight: '120px',
        border: '1px solid #E9E9EA',
        padding: '16px',
        boxSizing: 'border-box',
      }}
    >
      <Skeleton style={{ width: 28, height: 28, borderRadius: 4, marginBottom: 12 }} />
      <Skeleton style={{ width: 96, height: 12, marginBottom: 10 }} />
      <Skeleton style={{ width: 140, height: 24 }} />
    </div>
  )
}

export function ChartSkeleton() {
  return (
    <div className="w-full h-full flex flex-col justify-end gap-3">
      <div className="flex items-end justify-between gap-3 h-full px-2">
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={i} className="flex-1 flex items-end gap-1 h-full">
            <Skeleton
              className="flex-1"
              style={{ height: `${40 + ((i * 17) % 50)}%`, borderRadius: '6px 6px 0 0' }}
            />
            <Skeleton
              className="flex-1"
              style={{ height: `${30 + ((i * 11) % 40)}%`, borderRadius: '6px 6px 0 0' }}
            />
          </div>
        ))}
      </div>
    </div>
  )
}

export function NotificationSkeleton({ rows = 4 }: { rows?: number }) {
  return (
    <div className="space-y-4">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="flex items-start gap-3">
          <Skeleton style={{ width: 8, height: 8, borderRadius: 999, marginTop: 8 }} />
          <div className="flex-1 space-y-2">
            <Skeleton style={{ width: '90%', height: 14 }} />
            <Skeleton style={{ width: 80, height: 10 }} />
          </div>
        </div>
      ))}
    </div>
  )
}

export function TableSkeleton({ rows = 5, cols = 8 }: { rows?: number; cols?: number }) {
  return (
    <>
      {Array.from({ length: rows }).map((_, row) => (
        <tr key={row}>
          {Array.from({ length: cols }).map((__, col) => (
            <td
              key={col}
              className={`py-3 px-2 border-b border-gray-200 ${col === 0 ? 'border-l' : ''} ${col === cols - 1 ? 'border-r' : ''}`}
            >
              <Skeleton style={{ width: col === cols - 1 ? 48 : '80%', height: 14 }} />
            </td>
          ))}
        </tr>
      ))}
    </>
  )
}

export function ListCardSkeleton({ rows = 5 }: { rows?: number }) {
  return (
    <div className="space-y-4">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="flex items-center justify-between p-3 border border-gray-100 rounded-lg">
          <div className="flex items-center gap-3 flex-1">
            <Skeleton style={{ width: 40, height: 40, borderRadius: 8 }} />
            <div className="flex-1 space-y-2">
              <Skeleton style={{ width: '60%', height: 14 }} />
              <Skeleton style={{ width: '40%', height: 10 }} />
            </div>
          </div>
          <Skeleton style={{ width: 72, height: 28 }} />
        </div>
      ))}
    </div>
  )
}
